import { IAccountSettingsForm } from 'src/lib/schemas/account/settings';
import http from './http';
import { processCallback } from 'src/lib/utils/processCallback';
import { ConditionsResponse } from 'src/lib/schemas/account/conditions';
import {
	IAvailableChannels,
	ICompanyAccount,
	ICompanyAccountForm,
	ICompanyAccountUpdate,
	ICustomerProfile,
} from 'src/lib/schemas';

const apiVersion = 'v1';
const accountUrl = `/${apiVersion}/account`;

const updateCompanyAccount = async (
	payload: Partial<
		ICompanyAccountForm | ICompanyAccountUpdate | IAccountSettingsForm
	>,
): Promise<ICompanyAccount> => {
	const { account, ...rest } = payload as any;
	if ('id' in rest) {
		delete rest.id;
	}
	const { data } = await http.patch<ICompanyAccount>(
		`${accountUrl}/id/${account}`,
		{ ...rest },
	);
	return data;
};

const getCompanyAccount = async (
	accountId: string,
): Promise<ICompanyAccount> => {
	const { data } = await http.get<ICompanyAccount>(
		`${accountUrl}/id/${accountId}`,
	);
	return data;
};

const getAvailableChannels = async (
	accountId?: string,
): Promise<IAvailableChannels> => {
	const { data } = await http.get<IAvailableChannels>(
		`${accountUrl}/id/${accountId}/available_channels`,
	);
	return data;
};

const getAccountConfigOptions = async <T>(configName: string): Promise<T[]> => {
	try {
		const { data } = await http.get(`${accountUrl}/${configName}`);
		return data;
	} catch (error) {
		return [];
	}
};

const createAccountConfigOptions = async <T>(
	configName: string,
	payload: T,
) => {
	const { data } = await http.post(`${accountUrl}/${configName}`, payload);
	return data;
};

const updateAccountConfigOptions = async <T extends { id?: string }>(
	configName: string,
	payload: T,
) => {
	const { id, ...rest } = payload;
	const { data } = await http.patch(`${accountUrl}/${configName}/${id}`, rest);
	return data;
};

const deleteAccountConfigOptions = async (configName: string, id: string) => {
	await http.delete(`${accountUrl}/${configName}/${id}`);
};

const uploadFontFile = async (file: File) => {
	const formData = new FormData();
	formData.append('fontFile', file);

	const { data } = await http.post(`${accountUrl}/font-families`, formData, {
		headers: {
			'Content-Type': 'multipart/form-data',
		},
	});

	return data;
};

const getServiceableAreas = async (): Promise<any[]> => {
	const url = `/${apiVersion}/account/serviceable-areas/`;
	const { data } = await http.get(url);
	// If the API returns { data: [...] }, return data.data; otherwise, return data
	return Array.isArray(data) ? data : data.data || [];
};

// Location API for campaigns
export interface ICreateLocationPayload {
	name: string;
	address: {
		street: string;
		city: string;
		state: string;
		postalCode: string;
		country: string;
		lat: number;
		long: number;
		placeId?: string;
		regionCode?: string;
	};
	isDefault?: boolean;
	phone?: string;
}

const getLocations = async (): Promise<any[]> => {
	const url = `/${apiVersion}/account/locations`;
	const { data } = await http.get(url);
	return Array.isArray(data) ? data : data.data || [];
};

const createLocation = async (payload: ICreateLocationPayload): Promise<any> => {
	const url = `/${apiVersion}/account/locations`;
	const { data } = await http.post(url, payload);
	return data;
};

const updateLocation = async (id: string, payload: Partial<ICreateLocationPayload>): Promise<any> => {
	const url = `/${apiVersion}/account/locations/${id}`;
	const { data } = await http.patch(url, payload);
	return data;
};

const deleteLocation = async (id: string): Promise<void> => {
	const url = `/${apiVersion}/account/locations/${id}`;
	await http.delete(url);
};

const createServiceableArea = async (payload: any): Promise<any> => {
	const url = `/${apiVersion}/account/serviceable-areas/`;
	const { data } = await http.post(url, payload);
	return data;
};

const updateServiceableArea = async (
	id: string,
	payload: any,
): Promise<any> => {
	const url = `/${apiVersion}/account/serviceable-areas/${id}`;
	const { data } = await http.patch(url, payload);
	return data;
};

const deleteServiceableArea = async (id: string): Promise<void> => {
	const url = `/${apiVersion}/account/serviceable-areas/${id}`;
	await http.delete(url);
};

const getCustomerProfiles = async (): Promise<ICustomerProfile[]> => {
	const { data } = await http.get<ICustomerProfile[]>(
		`${accountUrl}/customer-profiles`,
	);

	return data;
};

const createCustomerProfile = async (
	payload: Omit<ICustomerProfile, '_id' | '__v' | 'account'>,
): Promise<ICustomerProfile> => {
	const { data } = await http.post<ICustomerProfile>(
		`${accountUrl}/customer-profiles`,
		payload,
	);
	return data;
};

const updateCustomerProfile = async (
	id: string,
	payload: Partial<Omit<ICustomerProfile, '_id' | '__v' | 'account'>>,
): Promise<ICustomerProfile> => {
	const { data } = await http.put<ICustomerProfile>(
		`${accountUrl}/customer-profiles/${id}`,
		payload,
	);
	return data;
};

const deleteCustomerProfile = async (id: string): Promise<void> => {
	await http.delete(`${accountUrl}/customer-profiles/${id}`);
};

const generateCustomerProfile = async (
	name: string,
	description: string,
): Promise<ICustomerProfile> => {
	const body = {
		inputs: {
			variables: { name, description },
		},
	};

	const { data } = await http.post(
		'/v2/apps/fusion_ai.generate_customer_profiles/execute/generate_customer_profiles',
		body,
	);

	const result = await processCallback(data.callback);
	return (result as any).output as ICustomerProfile;
};

const getConditions = async (): Promise<ConditionsResponse> => {
	const { data } = await http.get<ConditionsResponse>('/v1/conditions');
	return data;
};

export {
	getCompanyAccount,
	updateCompanyAccount,
	getAvailableChannels,
	getAccountConfigOptions,
	createAccountConfigOptions,
	updateAccountConfigOptions,
	deleteAccountConfigOptions,
	uploadFontFile,
	getServiceableAreas,
	createServiceableArea,
	updateServiceableArea,
	deleteServiceableArea,
	getLocations,
	createLocation,
	updateLocation,
	deleteLocation,
	getCustomerProfiles,
	createCustomerProfile,
	updateCustomerProfile,
	deleteCustomerProfile,
	generateCustomerProfile,
	getConditions,
};
